import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  NgZone
} from '@angular/core';

@Component({
  selector: 'nc-lightning-background',
  templateUrl: './lightning-background.component.html',
  styleUrls: ['./lightning-background.component.less']
})
export class LightningBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 闪电的色调（度数，0到360） */
  @Input() ncHue: number = 230;
  /** 闪电水平偏移量（-2~2） */
  @Input() ncXOffset: number = 0;
  /** 闪电动画速度倍率（0.5-2） */
  @Input() ncSpeed: number = 1;
  /** 闪电强度（0.1-2） */
  @Input() ncIntensity: number = 1;
  /** 闪电密集程度（0.1-3） */
  @Input() ncSize: number = 1;

  private canvas!: HTMLCanvasElement;
  private gl: WebGLRenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private rafId: number | null = null;
  private resizeObserver!: ResizeObserver;
  private startTime: number = 0;

  // WebGL Uniform locations
  private locResolution: WebGLUniformLocation | null = null;
  private locTime: WebGLUniformLocation | null = null;
  private locHue: WebGLUniformLocation | null = null;
  private locXOffset: WebGLUniformLocation | null = null;
  private locSpeed: WebGLUniformLocation | null = null;
  private locIntensity: WebGLUniformLocation | null = null;
  private locSize: WebGLUniformLocation | null = null;

  private vertexShaderSource = `
    attribute vec2 aPosition;
    void main() {
      gl_Position = vec4(aPosition, 0.0, 1.0);
    }
  `;

  private fragmentShaderSource = `
    precision mediump float;
    uniform vec2 iResolution;
    uniform float iTime;
    uniform float uHue;
    uniform float uXOffset;
    uniform float uSpeed;
    uniform float uIntensity;
    uniform float uSize;
    
    #define OCTAVE_COUNT 10

    vec3 hsv2rgb(vec3 c) {
        vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0,4.0,2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
        return c.z * mix(vec3(1.0), rgb, c.y);
    }

    float hash11(float p) {
        p = fract(p * .1031);
        p *= p + 33.33;
        p *= p + p;
        return fract(p);
    }

    float hash12(vec2 p) {
        vec3 p3 = fract(vec3(p.xyx) * .1031);
        p3 += dot(p3, p3.yzx + 33.33);
        return fract((p3.x + p3.y) * p3.z);
    }

    mat2 rotate2d(float theta) {
        float c = cos(theta);
        float s = sin(theta);
        return mat2(c, -s, s, c);
    }

    float noise(vec2 p) {
        vec2 ip = floor(p);
        vec2 fp = fract(p);
        float a = hash12(ip);
        float b = hash12(ip + vec2(1.0, 0.0));
        float c = hash12(ip + vec2(0.0, 1.0));
        float d = hash12(ip + vec2(1.0, 1.0));
        
        vec2 t = smoothstep(0.0, 1.0, fp);
        return mix(mix(a, b, t.x), mix(c, d, t.x), t.y);
    }

    float fbm(vec2 p) {
        float value = 0.0;
        float amplitude = 0.5;
        for (int i = 0; i < OCTAVE_COUNT; ++i) {
            value += amplitude * noise(p);
            p *= rotate2d(0.45);
            p *= 2.0;
            amplitude *= 0.5;
        }
        return value;
    }

    void mainImage( out vec4 fragColor, in vec2 fragCoord ) {
        vec2 uv = fragCoord / iResolution.xy;
        uv = 2.0 * uv - 1.0;
        uv.x *= iResolution.x / iResolution.y;
        uv.x += uXOffset;
        
        uv += 2.0 * fbm(uv * uSize + 0.8 * iTime * uSpeed) - 1.0;
        
        float dist = abs(uv.x);
        vec3 baseColor = hsv2rgb(vec3(uHue / 360.0, 0.7, 0.8));
        vec3 col = baseColor * pow(mix(0.0, 0.07, hash11(iTime * uSpeed)) / dist, 1.0) * uIntensity;
        col = pow(col, vec3(1.0));
        fragColor = vec4(col, 1.0);
    }

    void main() {
        mainImage(gl_FragColor, gl_FragCoord.xy);
    }
  `;

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.initWebGL();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // WebGL loops read parameters dynamically, no need for extra updates unless structural or texture-based changing.
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.gl && this.program) {
      this.gl.deleteProgram(this.program);
    }
  }

  private initWebGL(): void {
    const container = this.containerRef.nativeElement;

    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    container.appendChild(this.canvas);

    this.gl = this.canvas.getContext('webgl');
    if (!this.gl) {
      console.error('WebGL not supported');
      return;
    }

    const vertexShader = this.compileShader(this.vertexShaderSource, this.gl.VERTEX_SHADER);
    const fragmentShader = this.compileShader(this.fragmentShaderSource, this.gl.FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) return;

    this.program = this.gl.createProgram();
    if (!this.program) return;

    this.gl.attachShader(this.program, vertexShader);
    this.gl.attachShader(this.program, fragmentShader);
    this.gl.linkProgram(this.program);

    if (!this.gl.getProgramParameter(this.program, this.gl.LINK_STATUS)) {
      console.error('Program linking error:', this.gl.getProgramInfoLog(this.program));
      return;
    }

    this.gl.useProgram(this.program);

    const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const vertexBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, vertexBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);

    const aPosition = this.gl.getAttribLocation(this.program, 'aPosition');
    this.gl.enableVertexAttribArray(aPosition);
    this.gl.vertexAttribPointer(aPosition, 2, this.gl.FLOAT, false, 0, 0);

    this.locResolution = this.gl.getUniformLocation(this.program, 'iResolution');
    this.locTime = this.gl.getUniformLocation(this.program, 'iTime');
    this.locHue = this.gl.getUniformLocation(this.program, 'uHue');
    this.locXOffset = this.gl.getUniformLocation(this.program, 'uXOffset');
    this.locSpeed = this.gl.getUniformLocation(this.program, 'uSpeed');
    this.locIntensity = this.gl.getUniformLocation(this.program, 'uIntensity');
    this.locSize = this.gl.getUniformLocation(this.program, 'uSize');

    this.startTime = performance.now();

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
    });
    this.resizeObserver.observe(container);
    this.resizeCanvas();

    this.ngZone.runOutsideAngular(() => {
      this.render();
    });
  }

  private compileShader(source: string, type: number): WebGLShader | null {
    if (!this.gl) return null;
    const shader = this.gl.createShader(type);
    if (!shader) return null;
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', this.gl.getShaderInfoLog(shader));
      this.gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private resizeCanvas(): void {
    if (!this.canvas) return;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  private render = (): void => {
    if (!this.gl || !this.program) return;

    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.gl.uniform2f(this.locResolution, this.canvas.width, this.canvas.height);

    const currentTime = performance.now();
    this.gl.uniform1f(this.locTime, (currentTime - this.startTime) / 1000.0);
    this.gl.uniform1f(this.locHue, this.ncHue);
    this.gl.uniform1f(this.locXOffset, this.ncXOffset);
    this.gl.uniform1f(this.locSpeed, this.ncSpeed);
    this.gl.uniform1f(this.locIntensity, this.ncIntensity);
    this.gl.uniform1f(this.locSize, this.ncSize);

    this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);

    this.rafId = requestAnimationFrame(this.render);
  };
}
